import { CapabilityConfirmer } from "../capability-confirmer";
import type { BaseClient, RequestOptions } from "../client";
import type {
  ChannelConnection,
  ChannelConnectionDisconnectResult,
  ConnectLink,
  ConnectLinkCreateResult,
  ConnectLinkRevokeResult,
  CreateConnectLinkInput,
  ListConnectLinksOptions,
} from "../types/channels";
import type { ApiResponse, PaginatedResponse, PaginationOptions } from "../types/common";
import { CapabilityConfirmations } from "./capability-confirmations";

/** Mint, list, and revoke hosted connect links. */
class ChannelConnectLinks {
  constructor(
    private client: BaseClient,
    private confirmer: CapabilityConfirmer,
  ) {}

  /**
   * Mint a single-use hosted connect link. Returns HTTP 201.
   *
   * **The response's `data.url` contains the one-time link token EXACTLY
   * ONCE.** Send it to the person who should connect their account — an
   * idempotent replay (same `Idempotency-Key`) returns the link WITHOUT
   * `url`, so store it immediately (or revoke and mint a new link if lost).
   *
   * Requires the `channel.connect.manage` scope; OAuth callers additionally
   * need the workspace `admin` role.
   */
  async create(
    input: CreateConnectLinkInput,
    options?: RequestOptions,
  ): Promise<ApiResponse<ConnectLinkCreateResult>> {
    const resolved = await this.confirmer.prepare(
      { capabilityId: "channel.connect_link.create.execute", body: input },
      undefined,
      options,
    );
    return this.client.post("/api/v1/channels/connect-links", input, resolved);
  }

  /**
   * List the workspace's connect links (tokens are never returned), newest
   * first, with cursor-based pagination.
   *
   * `limit` defaults to 50 server-side and is capped at 100. Follow
   * `pagination.next_cursor` while `pagination.has_more` is true.
   *
   * The `channel_type` / `status` filters are applied **within** each page,
   * so a page may hold fewer than `limit` items while `has_more` is still
   * true — drive the loop off `has_more`, never off the item count.
   */
  async list(options?: ListConnectLinksOptions): Promise<PaginatedResponse<ConnectLink>> {
    const params: Record<string, string | undefined> = {};
    if (options?.limit !== undefined) params.limit = String(options.limit);
    if (options?.cursor) params.cursor = options.cursor;
    if (options?.channel_type) params.channel_type = options.channel_type;
    if (options?.status) params.status = options.status;
    return this.client.get("/api/v1/channels/connect-links", params);
  }

  /** Revoke a pending connect link so it can no longer be consumed. */
  async revoke(
    id: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<ConnectLinkRevokeResult>> {
    const resolved = await this.confirmer.prepare(
      { capabilityId: "channel.connect_link.revoke.execute", body: undefined },
      { id },
      options,
    );
    return this.client.delete(`/api/v1/channels/connect-links/${encodeURIComponent(id)}`, resolved);
  }
}

/** List and disconnect the workspace's channel connections. */
class ChannelConnections {
  constructor(
    private client: BaseClient,
    private confirmer: CapabilityConfirmer,
  ) {}

  /**
   * List the workspace's channel connections (generic, channel-agnostic
   * shape), newest first, with cursor-based pagination.
   *
   * `limit` defaults to 50 server-side and is capped at 100. Follow
   * `pagination.next_cursor` while `pagination.has_more` is true. Rows that
   * are not projectable as connections are dropped within the page, so a page
   * may hold fewer than `limit` items while `has_more` is still true — drive
   * the loop off `has_more`, never off the item count.
   */
  async list(options?: PaginationOptions): Promise<PaginatedResponse<ChannelConnection>> {
    const params: Record<string, string | undefined> = {};
    if (options?.limit !== undefined) params.limit = String(options.limit);
    if (options?.cursor) params.cursor = options.cursor;
    return this.client.get("/api/v1/channels/connections", params);
  }

  /**
   * Disconnect a connected channel account (best-effort platform logout, then
   * local revoke). Emits a `helpdesk.channel_disconnected` webhook event with
   * `reason: "api_disconnect"` if the account was previously connected.
   */
  async disconnect(
    id: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<ChannelConnectionDisconnectResult>> {
    const resolved = await this.confirmer.prepare(
      { capabilityId: "channel.connection.disconnect.execute", body: undefined },
      { id },
      options,
    );
    return this.client.delete(`/api/v1/channels/connections/${encodeURIComponent(id)}`, resolved);
  }
}

/**
 * Partner channel connect — mint hosted connect links that let an external
 * person (no Medal account required) attach a channel account (e.g.
 * `telegram_inbox`) to the workspace's helpdesk, and manage the resulting
 * connections.
 */
export class Channels {
  readonly connectLinks: ChannelConnectLinks;
  readonly connections: ChannelConnections;

  constructor(client: BaseClient, confirmer?: CapabilityConfirmer) {
    // Direct consumers (`new Channels(client)`) get a confirmer with no
    // client-level default: auto-confirm stays off unless a call opts in via
    // `{ autoConfirm: { previewSummary } }`.
    const resolved = confirmer ?? new CapabilityConfirmer(new CapabilityConfirmations(client));
    this.connectLinks = new ChannelConnectLinks(client, resolved);
    this.connections = new ChannelConnections(client, resolved);
  }
}
